import React, { useState, useEffect, useRef } from 'react';
import { ImageIcon } from '../../components/icons/Icons';
import { useSettings } from '../../context/SettingsContext';
import api from '../../services/api';
import AdminLayout from './AdminLayout';
import './Settings.css';

const Settings = () => {
  const { settings, refreshSettings } = useSettings();
  const messageRef = useRef(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await api.post('/settings/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, [field]: response.data.url }));
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to upload image' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('/settings', { settings: formData });
      await refreshSettings();
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
      setTimeout(() => messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Site Settings</h1>
      </div>

      {message.text && (
        <div ref={messageRef} className={`alert alert-${message.type}`}>{message.text}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="settings-grid">
          {/* Branding */}
          <div className="admin-card">
            <h3 className="admin-card-title">Branding</h3>
            
            <div className="form-group">
              <label className="form-label">Site Name</label>
              <input
                type="text"
                name="site_name"
                value={formData.site_name || ''}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tagline</label>
              <input
                type="text"
                name="site_tagline"
                value={formData.site_tagline || ''}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="settings-images">
              <div className="settings-image-group">
                <label className="form-label">Logo (Dark - for white background)</label>
                {formData.logo_url && (
                  <div className="settings-image-preview">
                    <img src={formData.logo_url} alt="Logo" />
                  </div>
                )}
                <label className="image-upload-btn">
                  <ImageIcon size={16} />
                  <span>Upload Dark Logo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'logo_url')}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              <div className="settings-image-group">
                <label className="form-label">Logo (Light - for blue background)</label>
                {formData.logo_light_url && (
                  <div className="settings-image-preview dark-bg">
                    <img src={formData.logo_light_url} alt="Logo Light" />
                  </div>
                )}
                <label className="image-upload-btn">
                  <ImageIcon size={16} />
                  <span>Upload Light Logo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'logo_light_url')}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              <div className="settings-image-group">
                <label className="form-label">Footer Logo</label>
                {formData.logo_footer_url && (
                  <div className="settings-image-preview dark-bg">
                    <img src={formData.logo_footer_url} alt="Footer Logo" />
                  </div>
                )}
                <label className="image-upload-btn">
                  <ImageIcon size={16} />
                  <span>Upload Footer Logo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'logo_footer_url')}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              <div className="settings-image-group">
                <label className="form-label">Favicon</label>
                {formData.favicon_url && (
                  <div className="settings-image-preview favicon">
                    <img src={formData.favicon_url} alt="Favicon" />
                  </div>
                )}
                <label className="image-upload-btn">
                  <ImageIcon size={16} />
                  <span>Upload Favicon</span>
                  <input
                    type="file"
                    accept="image/*,.ico"
                    onChange={(e) => handleImageUpload(e, 'favicon_url')}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Hero Backgrounds */}
          <div className="admin-card full-width">
            <h3 className="admin-card-title">Page Hero Backgrounds</h3>
            <p className="settings-help-text">Upload background images for each page's hero section. If not set, a gradient background will be used.</p>
            
            <div className="hero-backgrounds-grid">
              {[
                { key: 'hero_bg_home', label: 'Home Page' },
                { key: 'hero_bg_about', label: 'About Page' },
                { key: 'hero_bg_portfolio', label: 'Portfolio Page' },
                { key: 'hero_bg_impact', label: 'Impact Page' },
                { key: 'hero_bg_blog', label: 'Blog Page' },
                { key: 'hero_bg_contact', label: 'Contact Page' }
              ].map(({ key, label }) => (
                <div key={key} className="settings-image-group">
                  <label className="form-label">{label}</label>
                  {formData[key] && (
                    <div className="settings-image-preview hero-preview">
                      <img src={formData[key]} alt={label} />
                    </div>
                  )}
                  <label className="image-upload-btn">
                    <ImageIcon size={16} />
                    <span>Upload</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, key)}
                      style={{ display: 'none' }}
                    />
                  </label>
                  {formData[key] && (
                    <button 
                      type="button" 
                      className="btn-remove-image"
                      onClick={() => setFormData(prev => ({ ...prev, [key]: '' }))}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer Settings */}
          <div className="admin-card">
            <h3 className="admin-card-title">Footer Information</h3>
            
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="footer_email"
                value={formData.footer_email || ''}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="text"
                name="footer_phone"
                value={formData.footer_phone || ''}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Address</label>
              <input
                type="text"
                name="footer_address"
                value={formData.footer_address || ''}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Copyright Text</label>
              <input
                type="text"
                name="footer_copyright"
                value={formData.footer_copyright || ''}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>

          {/* Social Links */}
          <div className="admin-card">
            <h3 className="admin-card-title">Social Media Links</h3>
            
            <div className="form-group">
              <label className="form-label">LinkedIn URL</label>
              <input
                type="url"
                name="social_linkedin"
                value={formData.social_linkedin || ''}
                onChange={handleChange}
                className="form-input"
                placeholder="https://linkedin.com/company/..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Twitter/X URL</label>
              <input
                type="url"
                name="social_twitter"
                value={formData.social_twitter || ''}
                onChange={handleChange}
                className="form-input"
                placeholder="https://twitter.com/..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Facebook URL</label>
              <input
                type="url"
                name="social_facebook"
                value={formData.social_facebook || ''}
                onChange={handleChange}
                className="form-input"
                placeholder="https://facebook.com/..."
              />
            </div>
          </div>
        </div>

        <div className="settings-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
};

export default Settings;
